import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { CalendarClock } from 'lucide-react'
import { Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ConfirmButton, SectionLabel } from '@/components/bookings/dialogKit'
import { getBooking, getOpenSlots, rescheduleBooking } from '@/lib/api'
import { apiErrorReason, readApiError } from '@/lib/apiError'
import { shiftLocalDate } from '@/lib/calendar'
import { fmtDayDate } from '@/lib/format'
import { minutesFromLocalTime, minutesToLabel } from '@/lib/slotBooking'
import type { CalendarEvent } from '@/lib/render'
import { cn } from '@/lib/utils'

// ─── "Move this appointment" ───────────────────────────────────────────────────
//
// ONE dialog for both routes to a move, and that is the mechanism rather than a
// tidiness preference — a second component would be a second definition of what
// moving an appointment means, and the two would drift the way the two day grids
// drifted over the attendance mark:
//
//   * "Move to another time" in the booking sheet → opens with no proposal, so it
//     opens on the open-slot browser.
//   * a DRAG on the /schedule grid → arrives with `proposedStart` already filled
//     in, so it opens as a confirmation of that time (operator's choice,
//     2026-08-24: confirm before moving, not move-then-undo).
//
// THE TIME IS DIRECTLY EDITABLE (requested 2026-08-24: "the ability to edit the
// booking time when I click on the slot"). That is a deliberate loosening of the
// calendar's "every input is a CHOICE, never a typed value" rule, and it is
// contained the same way the payment dialog's amount field is: **the server owns
// every limit.** Business hours, per-resource schedules, buffers, closures and
// conflicts are all judged by `PATCH /:id/reschedule`, which refuses with
// `slot_unavailable` — so a typed time cannot put an appointment somewhere the
// business cannot serve it. The rule that still holds is the one about MONEY: no
// amount, no price, no payment method appears here.
//
// The open-slot chips stay alongside the fields, because they answer a different
// question — "when COULD this go?" — and they are still the fastest path.
//
// IT CANNOT CHANGE THE TREATMENT. `/reschedule` reuses the booking's own
// service_id and re-picks the machine, so "move it to another therapy" is a
// cancel plus a new booking. The copy says so rather than implying a capability
// the endpoint does not have.
//
// A CHECKED-IN SESSION CANNOT BE MOVED — the customer is in the building. The API
// refuses it and neither entry point offers it.

/** How far ahead to look for a new slot. One request, the whole window. */
const SEARCH_DAYS = 14

/** Naive tenant-local strings compare to the minute; seconds are noise here. */
function sameMinute(a: string | null, b: string | null): boolean {
  if (!a || !b) return false
  return a.slice(0, 16) === b.slice(0, 16)
}

export interface RescheduleDialogProps {
  event: CalendarEvent | null
  /**
   * The service to re-book. The booking keeps its own, so this is read-only
   * context used to ask what is free — and when the caller has none (a drag,
   * where a CalendarEvent carries the service name but not its id) the dialog
   * resolves it from the booking itself.
   */
  serviceId: string | null
  /**
   * A time the operator has already indicated, tenant-local naive
   * 'YYYY-MM-DDTHH:mm'. Set by a drag-and-drop; absent from the sheet.
   */
  proposedStart?: string | null
  onOpenChange: (open: boolean) => void
  onChanged?: () => void
}

export function RescheduleDialog({
  event,
  serviceId,
  proposedStart,
  onOpenChange,
  onChanged,
}: RescheduleDialogProps) {
  const open = event !== null

  const [slots, setSlots] = useState<{ start: string }[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  // The ONE value that gets sent. Both the exact fields and the slot chips write
  // here, so there is never a question of which input the button obeys.
  const [chosenStart, setChosenStart] = useState<string | null>(null)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Resolved from the booking when the caller could not supply one.
  const [resolvedServiceId, setResolvedServiceId] = useState<string | null>(null)

  const effectiveServiceId = serviceId ?? resolvedServiceId

  useEffect(() => {
    if (!open || !event) return
    setSlots(null)
    // A drag has already said where it wants to go, so that is the starting
    // choice; from the sheet nothing is chosen until the operator picks.
    setChosenStart(proposedStart ?? null)
    setReason('')
    setLoadError(null)
    setError(null)
    setBusy(false)
    setResolvedServiceId(null)
  }, [open, event?.id, proposedStart])

  // The service id, when the caller had none. One extra read on the drag path,
  // and only there.
  useEffect(() => {
    if (!open || !event || serviceId) return
    let live = true
    getBooking(event.id)
      .then((b) => { if (live) setResolvedServiceId(b.service_id) })
      // Availability stays unknown, so no chips and no "open slot" badge — but
      // the exact time still submits and the server still judges it. A failed
      // lookup must not block a move the API would accept.
      .catch(() => { /* exact-time path still works */ })
    return () => { live = false }
  }, [open, event?.id, serviceId])

  useEffect(() => {
    if (!open || !event || !effectiveServiceId) return
    let live = true
    setLoadError(null)
    setLoading(true)

    // Search forward from the day the appointment currently sits on: moving a
    // future booking usually means moving it later, and starting from today would
    // offer slots before the one being moved.
    const from = event.start.slice(0, 10)
    getOpenSlots({
      serviceId: effectiveServiceId,
      from: `${from}T00:00`,
      to: `${shiftLocalDate(from, SEARCH_DAYS)}T23:59`,
    })
      .then((res) => {
        if (!live) return
        setSlots((res?.available_slots ?? []).filter((s) => !sameMinute(s.start, event.start)))
      })
      .catch((e) => { if (live) setLoadError(readApiError(e)) })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [open, event?.id, event?.start, effectiveServiceId])

  // Days that actually have something free, in order. Derived from the slots
  // rather than from a calendar, so a day with nothing open is never offered.
  const days = useMemo(() => {
    const seen: string[] = []
    for (const slot of slots ?? []) {
      const date = slot.start.slice(0, 10)
      if (!seen.includes(date)) seen.push(date)
    }
    return seen
  }, [slots])

  // Which day's chips to show. Follows the CHOSEN time so editing the date field
  // re-aims the slot list at the same day — two controls pointing at different
  // days is how an operator picks a chip they did not mean.
  const chipDay = chosenStart?.slice(0, 10)
    ?? proposedStart?.slice(0, 10)
    ?? days[0]
    ?? event?.start.slice(0, 10)
    ?? null

  const timesForDay = useMemo(
    () => (slots ?? []).filter((s) => s.start.slice(0, 10) === chipDay),
    [slots, chipDay],
  )

  // Whether the chosen time is one availability actually offered. NOT a gate —
  // the server is the authority and a typed time is allowed through — but the
  // operator should know which of the two they are doing.
  const chosenIsOpenSlot = chosenStart !== null
    && (slots ?? []).some((s) => sameMinute(s.start, chosenStart))
  const slotsKnown = slots !== null

  const currentDate = event?.start.slice(0, 10) ?? ''
  const editDate = chosenStart?.slice(0, 10) ?? currentDate
  const editTime = chosenStart?.slice(11, 16) ?? event?.start.slice(11, 16) ?? ''
  const changed = event !== null && chosenStart !== null && !sameMinute(chosenStart, event.start)

  function setExact(date: string, time: string) {
    // Both halves are needed to name a time; an empty one means the operator is
    // mid-edit, so hold the previous choice rather than sending a partial value.
    if (!date || !time) return
    setChosenStart(`${date}T${time}`)
    setError(null)
  }

  async function confirm() {
    if (!event || !chosenStart || busy || !changed) return
    setBusy(true)
    setError(null)
    try {
      await rescheduleBooking(event.id, chosenStart, reason.trim() || undefined)
      toast.success(`${event.customer_name ?? 'Appointment'} moved`, {
        description: `${fmtDayDate(chosenStart.slice(0, 10))} at ${minutesToLabel(minutesFromLocalTime(chosenStart))}. The old slot is free again.`,
      })
      onChanged?.()
      onOpenChange(false)
    } catch (e) {
      const reasonCode = apiErrorReason(e)
      if (reasonCode === 'slot_unavailable') {
        // Either somebody took it between the read and the write, or a typed time
        // the business cannot serve (outside hours, a closure, no free machine).
        // Both mean: pick again from what is really open, and do not leave a
        // stale list on screen.
        setError('That time is not available — the machine is busy, or the centre is closed then. Pick one of the open slots below.')
        setChosenStart(null)
        setSlots(null)
      } else {
        setError(readApiError(e))
      }
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onOpenChange(false) }}>
      <DialogContent className="max-h-[85vh] max-w-md overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{proposedStart ? 'Move this appointment?' : 'Reschedule'}</DialogTitle>
          <DialogDescription>
            {event
              ? `${event.customer_name ?? 'Customer'} · ${event.service_name}`
              : ''}
          </DialogDescription>
        </DialogHeader>

        {event ? (
          <div className="flex flex-col gap-4">
            {/* ── From → to ───────────────────────────────────────────────── */}
            {/* Always shown, both entry points. A move is a change FROM something,
                and a dialog that named only the destination would leave the
                operator checking the grid behind it to see what they are undoing. */}
            <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/50 px-3 py-2">
              <div className="min-w-0 flex-1">
                <p className="text-[10.5px] font-semibold tracking-wide text-muted-foreground uppercase">Now</p>
                <p className="truncate text-[13px] font-semibold text-foreground">
                  {fmtDayDate(currentDate)}, {minutesToLabel(minutesFromLocalTime(event.start))}
                </p>
              </div>
              <span aria-hidden className="shrink-0 text-muted-foreground">→</span>
              <div className="min-w-0 flex-1">
                <p className="text-[10.5px] font-semibold tracking-wide text-muted-foreground uppercase">Moving to</p>
                <p className={cn(
                  'truncate text-[13px] font-semibold',
                  changed ? 'text-foreground' : 'text-muted-foreground',
                )}>
                  {chosenStart && changed
                    ? `${fmtDayDate(chosenStart.slice(0, 10))}, ${minutesToLabel(minutesFromLocalTime(chosenStart))}`
                    : 'Pick a time'}
                </p>
              </div>
            </div>

            {/* ── The exact time ──────────────────────────────────────────── */}
            <div className="flex flex-col gap-1.5">
              <SectionLabel>New date and time</SectionLabel>
              <div className="flex gap-1.5">
                <input
                  type="date"
                  value={editDate}
                  onChange={(e) => setExact(e.target.value, editTime)}
                  aria-label="New date"
                  className="min-w-0 flex-1 rounded-lg border border-border bg-background px-2.5 py-1.5 text-[14px] text-foreground outline-none focus:border-primary"
                />
                <input
                  type="time"
                  value={editTime}
                  // Steps in the tenant's own slot interval, so the arrows walk
                  // the same grid availability does. Any time is still typeable.
                  step={60}
                  onChange={(e) => setExact(editDate, e.target.value)}
                  aria-label="New time"
                  className="w-[124px] shrink-0 rounded-lg border border-border bg-background px-2.5 py-1.5 text-[14px] text-foreground outline-none focus:border-primary"
                />
              </div>
              {changed && slotsKnown ? (
                <p className={cn(
                  'text-[11.5px]',
                  chosenIsOpenSlot ? 'text-muted-foreground' : 'text-amber-700 dark:text-amber-400',
                )}>
                  {chosenIsOpenSlot
                    ? 'This is one of the open slots.'
                    : 'Not in the open slots below — the business hours, the machine schedule or a closure may rule it out. Saving will tell you.'}
                </p>
              ) : null}
            </div>

            {/* ── Or one of the real open slots ───────────────────────────── */}
            {loading ? (
              <div className="flex items-center gap-2 text-[12.5px] text-muted-foreground">
                <Loader2 className="size-4 animate-spin" aria-hidden />
                Finding what is free…
              </div>
            ) : loadError ? (
              <p className="text-[12px] text-muted-foreground">
                Couldn't load the open slots — you can still set an exact time above.
              </p>
            ) : !effectiveServiceId ? null : days.length === 0 ? (
              <p className="rounded-lg border border-dashed border-border px-3 py-2.5 text-[12px] text-muted-foreground">
                {event.service_name} has nothing free in the next {SEARCH_DAYS} days. Set an exact
                time above, or ask in chat to look further ahead.
              </p>
            ) : (
              <>
                <div className="flex flex-col gap-1.5">
                  <SectionLabel>Open days</SectionLabel>
                  <div className="flex flex-wrap gap-1.5">
                    {days.map((date) => (
                      <button
                        key={date}
                        type="button"
                        aria-pressed={chipDay === date}
                        // Selecting a DAY keeps the time, so the chips re-aim
                        // without silently changing what will be sent.
                        onClick={() => setExact(date, editTime)}
                        className={cn(
                          'rounded-lg border px-2.5 py-1.5 text-[12.5px] font-semibold transition-colors',
                          chipDay === date
                            ? 'border-primary bg-primary/10 text-foreground'
                            : 'border-border bg-card text-muted-foreground hover:bg-muted',
                        )}
                      >
                        {fmtDayDate(date)}
                      </button>
                    ))}
                  </div>
                </div>

                {timesForDay.length > 0 ? (
                  <div className="flex flex-col gap-1.5">
                    <SectionLabel>Open times</SectionLabel>
                    <div className="flex flex-wrap gap-1.5">
                      {timesForDay.map((slot) => (
                        <button
                          key={slot.start}
                          type="button"
                          aria-pressed={sameMinute(chosenStart, slot.start)}
                          onClick={() => { setChosenStart(slot.start.slice(0, 16)); setError(null) }}
                          className={cn(
                            'rounded-lg border px-2.5 py-1.5 text-[12.5px] font-semibold transition-colors',
                            sameMinute(chosenStart, slot.start)
                              ? 'border-primary bg-primary/10 text-foreground'
                              : 'border-border bg-card text-muted-foreground hover:bg-muted',
                          )}
                        >
                          {minutesToLabel(minutesFromLocalTime(slot.start))}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-[11.5px] text-muted-foreground">
                    Nothing free on {chipDay ? fmtDayDate(chipDay) : 'that day'} — pick another day above.
                  </p>
                )}
              </>
            )}

            {/* ── Reason ──────────────────────────────────────────────────── */}
            <div className="flex flex-col gap-1.5">
              <SectionLabel>Reason (optional)</SectionLabel>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Customer asked to move it…"
                aria-label="Reason"
                className="w-full rounded-lg border border-border bg-background px-2.5 py-1.5 text-[14px] text-foreground outline-none placeholder:text-muted-foreground focus:border-primary"
              />
            </div>

            {error ? <p className="text-[12.5px] text-destructive">{error}</p> : null}

            <div className="flex flex-col gap-2 border-t border-border pt-3">
              <ConfirmButton
                label={
                  changed && chosenStart
                    ? `Move to ${minutesToLabel(minutesFromLocalTime(chosenStart))}`
                    : 'Pick a new time'
                }
                icon={<CalendarClock className="size-4" aria-hidden />}
                busy={busy}
                disabled={!changed}
                onClick={() => void confirm()}
              />
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                disabled={busy}
                className="rounded-md border border-border bg-card px-3 py-1.5 text-[12.5px] font-semibold text-foreground hover:bg-muted disabled:opacity-40"
              >
                Keep it where it is
              </button>
              {/* The booking KEEPS ITS IDENTITY now — same row, same payments,
                  same credit. Saying so matters: a reschedule used to mint a
                  replacement and reset a paid session to unpaid. */}
              <p className="text-center text-[11px] text-muted-foreground">
                Same appointment and same treatment — its payment and any package credit move with
                it. The machine is reassigned automatically. To change the treatment, cancel and
                book the new one.
              </p>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
